import { useEffect, useRef, useContext, useCallback } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";

/**
 * Custom hook to intercept navigation attempts within a modal context
 * Uses React Router's NavigationContext to intercept before navigation happens
 *
 * @param {Object} config - Configuration object
 * @param {Array<string|RegExp>} config.interceptPatterns - Array of URL patterns to intercept
 * @param {Function} config.onIntercept - Callback when navigation is intercepted (receives target path)
 * @param {boolean} config.enabled - Whether interception is enabled
 */
export function useNavigationInterceptor({
  interceptPatterns = [],
  onIntercept,
  enabled = true,
}) {
  const { navigator } = useContext(NavigationContext);
  const interceptPatternsRef = useRef(interceptPatterns);
  const onInterceptRef = useRef(onIntercept);
  const enabledRef = useRef(enabled);

  // Update refs when props change
  useEffect(() => {
    interceptPatternsRef.current = interceptPatterns;
    onInterceptRef.current = onIntercept;
    enabledRef.current = enabled;
  }, [interceptPatterns, onIntercept, enabled]);

  // Check if URL matches any intercept pattern
  const matchesPattern = useCallback((path) => {
    if (!path || typeof path !== "string") return false;
    return interceptPatternsRef.current.some((pattern) => {
      if (typeof pattern === "string") {
        return path.includes(pattern);
      }
      if (pattern instanceof RegExp) {
        return pattern.test(path);
      }
      return false;
    });
  }, []);

  useEffect(() => {
    if (!enabledRef.current || !onInterceptRef.current || !navigator) {
      return;
    }

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    const interceptedPush = (...args) => {
      const [path] = args;
      const pathString = typeof path === "string" ? path : path?.pathname || "";

      if (matchesPattern(pathString)) {
        // Intercept navigation
        if (onInterceptRef.current) {
          onInterceptRef.current(pathString);
        }
        // Don't call original navigation
        return;
      }

      // Allow normal navigation
      return originalPush.apply(navigator, args);
    };

    const interceptedReplace = (...args) => {
      const [path] = args;
      const pathString = typeof path === "string" ? path : path?.pathname || "";

      if (matchesPattern(pathString)) {
        // Intercept navigation
        if (onInterceptRef.current) {
          onInterceptRef.current(pathString);
        }
        // Don't call original navigation
        return;
      }

      // Allow normal navigation
      return originalReplace.apply(navigator, args);
    };

    navigator.push = interceptedPush;
    navigator.replace = interceptedReplace;

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator, matchesPattern]);

  return { matchesPattern };
}
